import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  MenuItem
} from '@mui/material';
import { categoryService } from '../../services/categoryService';
import { CategoryData, CategoryOption, TypeOption } from '../../types/category';

interface CategoryTreeSelectorProps {
  selectedMainCategory?: string;
  selectedSubCategories: string[];
  selectedTypes: string[];
  selectedItems: string[];
  onMainCategoryChange?: (mainCategory: string) => void;
  onSubCategoryChange: (subCategories: string[]) => void;
  onTypeChange: (types: string[]) => void;
  onItemChange: (items: string[]) => void;
  showMainCategory?: boolean;
}

const CategoryTreeSelector: React.FC<CategoryTreeSelectorProps> = ({
  selectedMainCategory,
  selectedSubCategories,
  selectedTypes,
  selectedItems,
  onMainCategoryChange,
  onSubCategoryChange,
  onTypeChange,
  onItemChange,
  showMainCategory = true
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryData>({
    mainCategories: [],
    subCategoryOptions: [],
    allTypeOptions: [],
    itemOptions: {}
  });
  const [filteredSubCategories, setFilteredSubCategories] = useState<CategoryOption[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<TypeOption[]>([]);
  const [filteredItems, setFilteredItems] = useState<Record<string, string[]>>({});

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await categoryService.getAllCategories();
        
        if (response.message === 'Success') {
          const transformedData = categoryService.transformCategoriesForSelector(response);
          setCategoryData(transformedData);
        } else {
          setError('Failed to load categories');
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Auto-select first category when categories are loaded
  useEffect(() => {
    if (categoryData.mainCategories.length > 0 && !selectedMainCategory && onMainCategoryChange) {
      onMainCategoryChange(categoryData.mainCategories[0].value);
    }
  }, [categoryData.mainCategories, selectedMainCategory, onMainCategoryChange]);

  // Filter subcategories, types, and items based on selected main category
  useEffect(() => {
    if (!selectedMainCategory || !categoryData.subCategoryOptions.length) {
      setFilteredSubCategories([]);
      setFilteredTypes([]);
      setFilteredItems({});
      return;
    }

    // Filter subcategories that belong to the selected main category
    const filteredSubCats = categoryData.subCategoryOptions.filter(
      subCat => subCat.categoryId === selectedMainCategory
    );
    setFilteredSubCategories(filteredSubCats);

    // Filter types that belong to the selected main category
    const filteredTypes = categoryData.allTypeOptions.filter(
      type => type.categoryId === selectedMainCategory
    );
    setFilteredTypes(filteredTypes);

    // Filter items that belong to the selected main category's types
    const filteredItems: Record<string, string[]> = {};
    filteredTypes.forEach(type => {
      if (categoryData.itemOptions[type.value]) {
        filteredItems[type.value] = categoryData.itemOptions[type.value];
      }
    });
    setFilteredItems(filteredItems);
  }, [selectedMainCategory, categoryData]);

  // Handle main category change - clear all selections
  const handleMainCategoryChange = (newMainCategory: string) => {
    if (onMainCategoryChange) {
      onMainCategoryChange(newMainCategory);
    }
    
    // Clear all other selections when main category changes
    onSubCategoryChange([]);
    onTypeChange([]);
    onItemChange([]);
  };

  const handleSubCategoryToggle = (subCategory: string) => {
    const newSubCategories = selectedSubCategories.includes(subCategory) 
      ? selectedSubCategories.filter(s => s !== subCategory)
      : [...selectedSubCategories, subCategory];
    
    onSubCategoryChange(newSubCategories);
    
    // Remove types that don't belong to selected sub categories
    const validTypes = selectedTypes.filter(type => {
      const typeObj = filteredTypes.find(t => t.value === type);
      return typeObj && newSubCategories.includes(typeObj.subCategory);
    });
    
    if (validTypes.length !== selectedTypes.length) {
      onTypeChange(validTypes);
    }
  };

  const handleTypeToggle = (type: string) => {
    const newTypes = selectedTypes.includes(type) 
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    
    onTypeChange(newTypes);
  };

  const handleItemToggle = (item: string) => {
    const newItems = selectedItems.includes(item) 
      ? selectedItems.filter(i => i !== item)
      : [...selectedItems, item];
    
    onItemChange(newItems);
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading categories...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Main Category Section (Selectable) */}
      {showMainCategory && (
        <Box>
          <Typography
            sx={{
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: 1.2,
              color: '#181818',
              marginBottom: '16px'
            }}
          >
            Main Category
          </Typography>
          <TextField
            fullWidth
            select
            value={selectedMainCategory || ''}
            onChange={(e) => handleMainCategoryChange(e.target.value)}
            placeholder="Select a main category"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: selectedMainCategory ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          >
            {categoryData.mainCategories.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {/* Sub Category Section (Multi-select) */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: 1.2,
            color: '#181818',
            marginBottom: '8px'
          }}
        >
          Sub Category
        </Typography>
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.4,
            color: '#93989c',
            marginBottom: '16px'
          }}
        >
          Select the sub categories you specialize in (Food, Desserts, or Beverages)
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          {filteredSubCategories.map((subCat) => {
            const isSelected = selectedSubCategories.includes(subCat.value);
            
            return (
              <Chip
                key={subCat.value}
                label={subCat.label}
                onClick={() => handleSubCategoryToggle(subCat.value)}
                sx={{
                  backgroundColor: isSelected ? '#AC654D' : '#f6f8f8',
                  color: isSelected ? 'white' : '#181818',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  height: '39px',
                  borderRadius: '20px',
                  '&:hover': {
                    backgroundColor: isSelected ? '#9a5a42' : '#e8e8e8',
                    boxShadow: isSelected ? '0 4px 12px rgba(172, 101, 77, 0.3)' : 'none'
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Type Section (Multi-select) */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: 1.2,
            color: '#181818',
            marginBottom: '8px'
          }}
        >
          Type
        </Typography>
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.4,
            color: '#93989c',
            marginBottom: '16px'
          }}
        >
          {selectedSubCategories.length > 0 
            ? 'Select the types you specialize in' 
            : 'Please select at least one sub category first (options will be dimmed)'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          {filteredTypes.map((type) => {
            const isEnabled = selectedSubCategories.includes(type.subCategory);
            const isSelected = selectedTypes.includes(type.value);
            
            return (
              <Chip
                key={type.value}
                label={type.label}
                onClick={() => isEnabled && handleTypeToggle(type.value)}
                sx={{
                  backgroundColor: isSelected ? '#AC654D' : '#f6f8f8',
                  color: isSelected ? 'white' : '#181818',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  height: '39px',
                  borderRadius: '20px',
                  opacity: isEnabled ? 1 : 0.4,
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                  '&:hover': {
                    backgroundColor: isEnabled 
                      ? (isSelected ? '#9a5a42' : '#e8e8e8')
                      : '#f6f8f8',
                    boxShadow: isEnabled && isSelected ? '0 4px 12px rgba(172, 101, 77, 0.3)' : 'none'
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Items Section */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: 1.2,
            color: '#181818',
            marginBottom: '8px'
          }}
        >
          Items
        </Typography>
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.4,
            color: '#93989c',
            marginBottom: '16px'
          }}
        >
          {selectedTypes.length > 0
            ? 'Select the items you offer (other items are dimmed)' 
            : 'Please select at least one type first (all items are dimmed)'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          {Object.entries(filteredItems).flatMap(([typeKey, items]) => 
            (items as string[]).map((item: string) => {
              const isEnabled = selectedTypes.includes(typeKey);
              const isSelected = selectedItems.includes(item);
              
              return (
                <Chip
                  key={`${typeKey}-${item}`}
                  label={item}
                  onClick={() => isEnabled && handleItemToggle(item)}
                  sx={{
                    backgroundColor: isSelected ? '#AC654D' : '#f6f8f8',
                    color: isSelected ? 'white' : '#181818',
                    fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    height: '39px',
                    borderRadius: '20px',
                    opacity: isEnabled ? 1 : 0.4,
                    cursor: isEnabled ? 'pointer' : 'not-allowed',
                    '&:hover': {
                      backgroundColor: isEnabled 
                        ? (isSelected ? '#9a5a42' : '#e8e8e8')
                        : '#f6f8f8',
                      boxShadow: isEnabled && isSelected ? '0 4px 12px rgba(172, 101, 77, 0.3)' : 'none'
                    }
                  }}
                />
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryTreeSelector;
